// Frontend mirror of backend/utils/customerFeeCatalog.js.
//
// This is the SIMPLIFIED catalog (aligned with the active xlsx fee
// schedule). Only four fee types are active:
//   - storage_fee       per pallet monthly, with storage_class variants
//   - handling_inbound  per pallet, includes serial/MAC/relabel service
//   - handling_outbound per pallet, includes pick/pack/ship service
//   - labour            ad-hoc hourly work
//
// Every other fee type stays in the catalog under a hidden `legacy`
// group so historical rows still resolve their label / description
// when rendering read-only displays. Active fee pickers filter on
// `hidden: true` to omit legacy categories.
//
// TODO (post-demo): replace this file with a fetch against
// `GET /api/customer-fees/catalog` and cache it in React Query so
// both files stay in lockstep automatically. Until then, whenever
// you change one file, update the other.

export const BILLING_UNITS = [
  { value: 'per_pallet',    label: 'Per Pallet' },
  { value: 'per_carton',    label: 'Per Carton' },
  { value: 'per_line_item', label: 'Per Line Item' },
  { value: 'per_order',     label: 'Per Order' },
  { value: 'per_kit',       label: 'Per Kit' },
  { value: 'per_unit',      label: 'Per Unit' },
  { value: 'per_hour',      label: 'Per Hour' }
];

export const FEE_SCHEDULES = [
  { value: 'one_time',  label: 'One-time' },
  { value: 'daily',     label: 'Daily' },
  { value: 'weekly',    label: 'Weekly' },
  { value: 'monthly',   label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually',  label: 'Annually' }
];

export const STORAGE_CLASSES = [
  { value: 'standard',   label: 'Standard Pallet (40"×48"×60")' },
  { value: 'oversize',   label: 'Oversized Pallet (45"×45"×75")' },
  { value: 'rackable',   label: 'Rackable' },
  { value: 'floor_only', label: 'Floor Only' },
  { value: 'hazmat',     label: 'Hazmat' }
];

export const FEE_CATEGORIES = [
  {
    id: 'storage',
    label: 'Storage',
    description: 'Monthly pallet storage. Loose cartons roll up into pallet-equivalents by volume (cubic feet / 67) and are billed at the same per-pallet rate.',
    fees: [
      {
        value: 'storage_fee',
        label: 'Monthly Storage',
        description: 'Per-pallet monthly storage fee. One row per storage class (standard + oversize typical).',
        defaultBillingUnit: 'per_pallet',
        defaultFeeSchedule: 'monthly',
        requiresStorageClass: true,
        supportsDiscounts: true
      }
    ]
  },
  {
    id: 'handling',
    label: 'Handling',
    description: 'Per-pallet handling fees. Inbound handling covers all serial/MAC/relabel work. Outbound handling covers all pick/pack/ship work.',
    fees: [
      {
        value: 'handling_inbound',
        label: 'Handling — Inbound',
        description: 'Charged per pallet received. Loose cartons roll up into pallet-equivalents by volume. Serial capture, MAC capture, and relabel work are all included (no separate fee).',
        defaultBillingUnit: 'per_pallet',
        defaultFeeSchedule: 'one_time',
        requiresStorageClass: false,
        supportsDiscounts: false
      },
      {
        value: 'handling_outbound',
        label: 'Handling — Outbound',
        description: 'Charged per pallet shipped. Includes picking, packing, and shipping coordination (no separate fees).',
        defaultBillingUnit: 'per_pallet',
        defaultFeeSchedule: 'one_time',
        requiresStorageClass: false,
        supportsDiscounts: false
      },
      {
        value: 'labour',
        label: 'Additional Labour',
        description: 'Hourly rate for ad-hoc warehouse work outside of inbound/outbound handling. Applied manually by ops as needed.',
        defaultBillingUnit: 'per_hour',
        defaultFeeSchedule: 'one_time',
        requiresStorageClass: false,
        supportsDiscounts: false
      }
    ]
  },
  {
    id: 'insurance',
    label: 'Insurance',
    // RF8: active again -- rate per $10,000 of declared in-stock value
    // above the $50k included coverage, billed on the monthly storage
    // invoice. Unconfigured = not offered (opt-in per customer).
    fees: [
      { value: 'insurance', label: 'Additional Insurance', description: 'Rate per $10,000 of declared in-stock value above the $50,000 included coverage. Billed monthly with storage. Leave unconfigured to not offer insurance.', defaultBillingUnit: 'per_unit', defaultFeeSchedule: 'monthly', requiresStorageClass: false, supportsDiscounts: false }
    ]
  },

  // Legacy / hidden. Kept so historical rows resolve a label.
  {
    id: 'legacy_receiving_options',
    label: 'Legacy — Inbound Receiving Options',
    description: 'Included in Handling — Inbound. Still available as ASN form checkboxes for warehouse instructions.',
    hidden: true,
    fees: [
      { value: 'serial_capture', label: 'Serial Number Capture', description: 'Included in Handling — Inbound.', defaultBillingUnit: 'per_unit', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'mac_capture',    label: 'MAC Address Capture',   description: 'Included in Handling — Inbound.', defaultBillingUnit: 'per_unit', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'relabel',        label: 'Relabel on Receipt',    description: 'Included in Handling — Inbound.', defaultBillingUnit: 'per_unit', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true }
    ]
  },
  {
    id: 'legacy_fulfillment',
    label: 'Legacy — Fulfillment',
    description: 'Granular pick/pack/ship fees now covered by Handling — Outbound.',
    hidden: true,
    fees: [
      { value: 'order_picking',     label: 'Order Picking',             description: 'Included in Handling — Outbound.', defaultBillingUnit: 'per_order', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'picking_fee',       label: 'Picking Fee (legacy)',      description: 'Superseded by Handling — Outbound.', defaultBillingUnit: 'per_order', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'packing_fee',       label: 'Packing',                   description: 'Included in Handling — Outbound.', defaultBillingUnit: 'per_order', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'shipping_fee',      label: 'Shipping',                  description: 'Included in Handling — Outbound.', defaultBillingUnit: 'per_order', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'asset_tagging',     label: 'Asset Tagging and Staging', description: 'Included in Handling — Outbound when applicable.', defaultBillingUnit: 'per_unit', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'phased_deployment', label: 'Phased Deployment',         description: 'Included in Handling — Outbound when applicable.', defaultBillingUnit: 'per_order', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'reboxing',          label: 'Reboxing',                  description: 'Included in Handling — Outbound.', defaultBillingUnit: 'per_unit', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true }
    ]
  },
  {
    id: 'legacy_kitting',
    label: 'Legacy — Kitting & Assembly',
    description: 'Kit build and assembly work. Ops invoices manually until these come back as first-class.',
    hidden: true,
    fees: [
      { value: 'kitting_fee',  label: 'Kit Build', description: 'Invoice manually via Additional Labour until re-enabled.', defaultBillingUnit: 'per_kit',  defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'assembly_fee', label: 'Assembly',  description: 'Invoice manually via Additional Labour until re-enabled.', defaultBillingUnit: 'per_unit', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true }
    ]
  },
  {
    id: 'legacy_technical',
    label: 'Legacy — Technical Services',
    description: 'Device-level technical services. Ops invoices manually until these come back as first-class.',
    hidden: true,
    fees: [
      { value: 'firmware_updates',    label: 'Firmware Updates',      description: 'Invoice manually until re-enabled.', defaultBillingUnit: 'per_unit', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'software_updates',    label: 'Software Updates',      description: 'Invoice manually until re-enabled.', defaultBillingUnit: 'per_unit', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'product_testing',     label: 'Product Testing',       description: 'Invoice manually until re-enabled.', defaultBillingUnit: 'per_unit', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'product_preparation', label: 'Product Preparation',   description: 'Invoice manually until re-enabled.', defaultBillingUnit: 'per_unit', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true }
    ]
  },
  {
    id: 'legacy_other',
    label: 'Legacy — Other',
    description: 'Returns handling, insurance, and deprecated receiving fees from older rate cards.',
    hidden: true,
    fees: [
      { value: 'returns_handling', label: 'Returns Handling',                  description: 'Invoice manually until re-enabled.', defaultBillingUnit: 'per_unit',  defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'special_project',  label: 'Special Project',                   description: 'Invoice manually as needed.', defaultBillingUnit: 'per_hour',  defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'receiving_pallet', label: 'Receiving — Pallet (legacy)',       description: 'Superseded by Handling — Inbound.', defaultBillingUnit: 'per_pallet', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true },
      { value: 'receiving_carton', label: 'Receiving — Loose Cartons (legacy)',description: 'Superseded by Handling — Inbound (loose cartons roll up by volume).', defaultBillingUnit: 'per_carton', defaultFeeSchedule: 'one_time', requiresStorageClass: false, supportsDiscounts: false, deprecated: true }
    ]
  }
];

// Flat list of non-hidden fees for forms that want a single ordered
// array rather than grouped categories. Each entry carries its
// category label for rendering headers.
export const FEE_TYPES_FLAT = FEE_CATEGORIES
  .filter((cat) => !cat.hidden)
  .flatMap((cat) => cat.fees.map((fee) => ({ ...fee, category: cat.label, categoryId: cat.id })));

export const FEE_INDEX = FEE_CATEGORIES.reduce((acc, cat) => {
  for (const fee of cat.fees) {
    acc[fee.value] = { ...fee, category: cat.label, categoryId: cat.id, hiddenCategory: !!cat.hidden };
  }
  return acc;
}, {});

export const getFeeMetadata = (value) => FEE_INDEX[value] || null;

export const getActiveCategories = () => FEE_CATEGORIES.filter((cat) => !cat.hidden);
